<template>
  <div
    @click.self="e => $emit('click', e)"
    class="app-full-wrapper"
    :class="{
      blur,
      transparent,
      closed: state === STATE_CLOSED,
      closing: state === STATE_CLOSING,
      opening: state === STATE_OPENING,
      opened: state === STATE_OPENED,
    }"
    :style="{
      'z-index': zIndex,
    }"
  >
    <slot
      v-if="!destroyOnClose || (destroyOnClose && state !== STATE_CLOSED)"
    />
  </div>
</template>

<script>
const STATE_CLOSED = 0
const STATE_CLOSING = 1
const STATE_OPENING = 2
const STATE_OPENED = 3

export default {
  name: 'AppFullWrapper',

  data() {
    this.STATE_CLOSED = STATE_CLOSED
    this.STATE_CLOSING = STATE_CLOSING
    this.STATE_OPENING = STATE_OPENING
    this.STATE_OPENED = STATE_OPENED

    return {
      transitionDuration: 400,
      state: STATE_CLOSED,
      onOpenTimer: undefined,
      onCloseTimer: undefined,
    }
  },

  props: {
    destroyOnClose: {
      type: Boolean,
      default: false,
    },

    modelValue: {
      type: Boolean,
      default: false,
    },

    blur: {
      type: Boolean,
      default: false,
    },
  },

  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        value ? this.open() : this.close()
      },
    },
  },

  computed: {
    zIndex() {
      return (
        app.activeWrappers.findIndex(wrapperId => wrapperId === this._uid) + 10
      )
    },

    transparent() {
      return app.getCurrentFullWrapper() !== this._uid
    },
  },

  mounted() {
    const wrapper = this.$el

    wrapper.addEventListener('transitionend', event => {
      if (event.propertyName !== 'background-color') return

      switch (this.state) {
        case STATE_OPENING:
          if (this.onOpenTimer) clearTimeout(this.onOpenTimer)

          this.state = STATE_OPENED
          this.$emit('opened', this)
          break

        case STATE_CLOSING:
          if (this.onCloseTimer) clearTimeout(this.onCloseTimer)

          this.state = STATE_CLOSED
          this.$emit('closed', this)
          break
      }
    })

    document.body.appendChild(wrapper)
  },

  beforeCreate() {
    this.$on('opening', () => {
      this.state = STATE_OPENING
      app.activeWrappers.push(this._uid)
      this.disableScroll()
    })

    this.$on('opened', () => {
      this.state = STATE_OPENED
    })

    this.$on('closing', () => {
      this.state = STATE_CLOSING
    })

    this.$on('closed', () => {
      this.state = STATE_CLOSED
      this.enableScroll()
      this.removeWrapper()
    })
  },

  unmounted() {
    this.removeWrapper()
    this.enableScroll()

    const parentNode = this.$el.parentNode

    if (parentNode) parentNode.removeChild(this.$el)
  },

  methods: {
    disableScroll() {
      document.body.style.cssText = `
        position: fixed;
        top: -${window.scrollY}px;
      `
    },

    enableScroll() {
      const bodyTop = document.body.style.top

      if (!bodyTop) return

      const bodyScrollY = parseFloat(bodyTop || 0)

      document.body.style.cssText = ''

      window.scrollTo(0, bodyScrollY * -1)
    },

    removeWrapper() {
      app.activeWrappers = app.activeWrappers.filter(
        wrapperId => wrapperId !== this._uid,
      )
    },

    async open() {
      return new Promise(resolve => {
        if ([STATE_OPENING, STATE_OPENED].includes(this.state)) return resolve()

        this.$once('opened', resolve)
        this.$emit('opening', this)

        this.onOpenTimer = setTimeout(
          () => this.$emit('opened', this),
          this.transitionDuration,
        )
      })
    },

    async close() {
      return new Promise((resolve, reject) => {
        if ([STATE_CLOSING, STATE_CLOSED].includes(this.state)) return resolve()

        this.$once('closed', resolve)
        this.$emit('closing', this)

        this.onCloseTimer = setTimeout(
          () => this.$emit('closed', this),
          this.transitionDuration,
        )
      })
    },
  },
}
</script>

<style lang="scss" scoped>
$transitionDuration: 400ms;

.app-full-wrapper {
  overflow: auto;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition: background-color $transitionDuration;
  z-index: 10;
  overscroll-behavior: contain;

  &.blur {
    backdrop-filter: blur(5px);
  }

  &.opening,
  &.opened {
    background-color: rgba(0, 0, 0, 0.7);
  }

  &.transparent {
    background-color: transparent;
  }

  &.closed {
    z-index: -1 !important;
    overflow: hidden;
  }
}
</style>
